"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import * as THREE from "three";
import type { AppCopy, Language } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const headingVariants = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.8,
      staggerChildren: 0.045,
    },
  },
};

const letterVariants = {
  hidden: { opacity: 0, y: 34, filter: "blur(5px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9],
    },
  },
};

/** Allows LRM/RLM (RTL headlines) and apostrophes (e.g. "We're"). */
const LATIN_WORD_PATTERN = /^[A-Za-z0-9&.+\-'\u200e\u200f]+$/;

interface WovenLightHeroProps {
  copy: AppCopy["hero"];
  language: Language;
}

export const WovenLightHero = ({ copy, language }: WovenLightHeroProps) => {
  const headline = copy.headline;
  const words = headline.split(" ");

  return (
    <section
      id="home"
      className="relative flex h-screen w-full scroll-mt-14 flex-col items-center justify-center overflow-hidden bg-white transition-colors dark:bg-black"
    >
      <WovenCanvas />
      <div className="relative z-40 w-full px-4 text-center">
        <motion.h1
          initial="hidden"
          animate="visible"
          variants={headingVariants}
          className={cn(
            "text-6xl font-semibold leading-[1.12] text-slate-950 drop-shadow-[0_18px_55px_rgba(15,23,42,0.18)] md:text-8xl dark:text-white dark:drop-shadow-[0_0_50px_rgba(255,255,255,0.28)]",
            language === "en"
              ? "uppercase tracking-[0.04em]"
              : "tracking-[-0.02em]",
          )}
        >
          {words.map((word, i) => {
            const isLatinWord = LATIN_WORD_PATTERN.test(word);
            return (
            <span
              key={`${word}-${i}`}
              className="inline-block"
              dir={isLatinWord ? "ltr" : undefined}
              style={isLatinWord ? { unicodeBidi: "isolate" } : undefined}
            >
              {word.split("").map((char, j) => (
                <motion.span
                  key={`${char}-${i}-${j}`}
                  variants={letterVariants}
                  style={{ display: "inline-block" }}
                >
                  {char}
                </motion.span>
              ))}
              {i < words.length - 1 && <span>&nbsp;</span>}
            </span>
            );
          })}
        </motion.h1>

        <p
          className="mx-auto mt-6 max-w-xl text-lg font-medium leading-8 text-slate-700 drop-shadow-[0_10px_30px_rgba(255,255,255,0.45)] dark:text-slate-100 dark:drop-shadow-[0_0_24px_rgba(255,255,255,0.16)]"
        >
          {copy.subtitle}
        </p>

        <div className="mt-10">
          <a
            href="#about"
            className="inline-block rounded-full border-2 border-slate-900/10 bg-slate-950 px-8 py-3 font-semibold text-white shadow-xl shadow-slate-900/10 backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:bg-slate-800 dark:border-white/20 dark:bg-white/10 dark:text-white dark:shadow-white/5 dark:hover:bg-white/20"
          >
            {copy.cta}
          </a>
        </div>
      </div>
    </section>
  );
};

const WovenCanvas = () => {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    const container = mountRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000,
    );
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    const particleCanvas = document.createElement("canvas");
    particleCanvas.width = particleCanvas.height = 64;
    const context = particleCanvas.getContext("2d");
    if (!context) return;

    const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, "rgba(255,255,255,1)");
    gradient.addColorStop(0.3, "rgba(210,225,255,0.85)");
    gradient.addColorStop(0.65, "rgba(180,200,255,0.3)");
    gradient.addColorStop(1, "rgba(255,255,255,0)");
    context.fillStyle = gradient;
    context.fillRect(0, 0, 64, 64);
    const particleTexture = new THREE.CanvasTexture(particleCanvas);

    const particleCount = 32000;
    const positions = new Float32Array(particleCount * 3);
    const originalPositions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);

    const torusKnot = new THREE.TorusKnotGeometry(1.55, 0.44, 280, 36);
    const torusArray = torusKnot.attributes.position.array;
    const torusCount = torusKnot.attributes.position.count;

    for (let i = 0; i < particleCount; i += 1) {
      const vertexIndex = (i % torusCount) * 3;
      const jitter = 0.07;
      const x = torusArray[vertexIndex] + (Math.random() - 0.5) * jitter;
      const y = torusArray[vertexIndex + 1] + (Math.random() - 0.5) * jitter;
      const z = torusArray[vertexIndex + 2] + (Math.random() - 0.5) * jitter;

      positions[i * 3] = originalPositions[i * 3] = x;
      positions[i * 3 + 1] = originalPositions[i * 3 + 1] = y;
      positions[i * 3 + 2] = originalPositions[i * 3 + 2] = z;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      size: 0.034,
      map: particleTexture,
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthWrite: false,
      opacity: 0.78,
      color: 0xffffff,
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    const syncParticleTheme = () => {
      const isDark = document.documentElement.classList.contains("dark");
      material.color.setHex(isDark ? 0xffffff : 0x2563eb);
      material.opacity = isDark ? 0.78 : 0.42;
      material.blending = isDark ? THREE.AdditiveBlending : THREE.NormalBlending;
      material.needsUpdate = true;
    };

    syncParticleTheme();

    const themeObserver = new MutationObserver(syncParticleTheme);
    themeObserver.observe(document.documentElement, {
      attributeFilter: ["class"],
      attributes: true,
    });

    const spring = 0.046;
    const damping = 0.872;
    const repulsionRadius = 1.6;
    const repulsionRadiusSq = repulsionRadius * repulsionRadius;
    const repulsionForce = 0.024;
    const undulationAmplitude = 0.028;
    const undulationFrequency = 0.55;

    const startTime = performance.now();
    const pointer = { x: 0, y: 0 };
    let pointerActive = false;
    let animationFrame = 0;

    const pointerLocal = new THREE.Vector3();
    const inverseMatrix = new THREE.Matrix4();

    const verticalFov = THREE.MathUtils.degToRad(camera.fov);
    let halfHeight = Math.tan(verticalFov / 2) * camera.position.z;
    let halfWidth = halfHeight * camera.aspect;

    const updatePointer = (x: number, y: number) => {
      pointerActive = true;
      pointer.x = (x / window.innerWidth) * 2 - 1;
      pointer.y = -(y / window.innerHeight) * 2 + 1;
    };

    const handlePointerMove = (event: PointerEvent) => {
      updatePointer(event.clientX, event.clientY);
    };

    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches[0]) {
        updatePointer(event.touches[0].clientX, event.touches[0].clientY);
      }
    };

    const handlePointerLeave = () => {
      pointerActive = false;
      pointer.x = 0;
      pointer.y = 0;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("touchmove", handleTouchMove, { passive: true });
    window.addEventListener("pointerleave", handlePointerLeave);

    const animate = () => {
      animationFrame = window.requestAnimationFrame(animate);
      const elapsed = (performance.now() - startTime) * 0.001;

      inverseMatrix.copy(points.matrixWorld).invert();
      pointerLocal
        .set(pointer.x * halfWidth, pointer.y * halfHeight, 0)
        .applyMatrix4(inverseMatrix);

      const px = pointerLocal.x;
      const py = pointerLocal.y;
      const pz = pointerLocal.z;

      for (let i = 0; i < particleCount; i += 1) {
        const offset = i * 3;
        const cx = positions[offset];
        const cy = positions[offset + 1];
        const cz = positions[offset + 2];
        let vx = velocities[offset];
        let vy = velocities[offset + 1];
        let vz = velocities[offset + 2];
        const ox = originalPositions[offset];
        const oy = originalPositions[offset + 1];
        const oz = originalPositions[offset + 2];

        const undulation =
          Math.sin(elapsed * undulationFrequency + ox * 1.8 + oy * 0.9) *
          undulationAmplitude;
        const tx = ox + undulation * 0.6;
        const ty = oy + undulation;
        const tz = oz + undulation * 0.4;

        vx += (tx - cx) * spring;
        vy += (ty - cy) * spring;
        vz += (tz - cz) * spring;

        if (pointerActive) {
          const dx = cx - px;
          const dy = cy - py;
          const dz = cz - pz;
          const distanceSq = dx * dx + dy * dy + dz * dz;

          if (distanceSq < repulsionRadiusSq && distanceSq > 0.0001) {
            const distance = Math.sqrt(distanceSq);
            const force =
              ((repulsionRadius - distance) * repulsionForce) / distance;
            vx += dx * force;
            vy += dy * force;
            vz += dz * force;
          }
        }

        vx *= damping;
        vy *= damping;
        vz *= damping;

        positions[offset] = cx + vx;
        positions[offset + 1] = cy + vy;
        positions[offset + 2] = cz + vz;
        velocities[offset] = vx;
        velocities[offset + 1] = vy;
        velocities[offset + 2] = vz;
      }

      geometry.attributes.position.needsUpdate = true;

      points.rotation.x +=
        (Math.sin(elapsed * 0.45) * 0.08 + pointer.y * 0.25 - points.rotation.x) *
        0.04;
      points.rotation.y +=
        (elapsed * 0.16 + pointer.x * 0.45 - points.rotation.y) * 0.04;
      points.position.x += (pointer.x * 0.3 - points.position.x) * 0.03;
      points.position.y += (pointer.y * 0.1 - points.position.y) * 0.03;
      points.position.z = Math.sin(elapsed * 0.9) * 0.12;
      points.scale.setScalar(0.86 + Math.sin(elapsed * 1.1) * 0.02);

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      halfHeight = Math.tan(verticalFov / 2) * camera.position.z;
      halfWidth = halfHeight * camera.aspect;
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("pointerleave", handlePointerLeave);
      themeObserver.disconnect();

      window.cancelAnimationFrame(animationFrame);
      torusKnot.dispose();
      geometry.dispose();
      material.dispose();
      particleTexture.dispose();
      renderer.dispose();

      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={mountRef} className="pointer-events-none absolute inset-0 z-0" />;
};
